import Container from '@/components/Container';
import SubHeading from '@/components/SubHeading';
import ScrollSpy, { ScrollSpyItem } from '@/components/ScrollSpy';
import SecondaryTitle from '@/components/SecondaryTitle';
import Lead from '@/components/Lead';
import LinkMore from '@/components/LinkMore';
import Check from '@/components/Check';
import Paragraph from '@/components/Paragraph';
import Image from '@/components/Image/RemoteImage';
import PartnersApplicationSteps from '@/domain/Partners/PartnersApplicationSteps';
import PartnersHeroAnimation from '@/domain/Partners/PartnersHeroAnimation';
import PartnersTestimonials from '@/domain/Partners/PartnersTestimonials';
import PartnersClientBrands from '@/domain/Partners/PartnersClientBrands';
import PartnerApplyForm from '@/domain/Forms/PartnerApplyForm';

export default function PartnersProgramPage() {
  return (
    <div className="bg-gray-100">
      <div className="bg-gradient-to-tl from-green-200 to-green-50 py-10 lg:py-20 xl:py-32 relative overflow-hidden">
        <Container className="py-10 md:py-0">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-6 space-y-6 lg:pr-20 relative z-20">
              <SubHeading heading="Programa de socios" />
              <h1 className="text-4xl lg:text-5xl font-display font-extrabold tracking-tight">
                Hagamos crecer juntos la gran familia que somos
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed lg:pr-10">
                Como parte de nuestra comunidad global, con nuestros socios trabajamos
                juntos para acelerar el impacto en la adopción de la cultura ágil de
                nuestros clientes, mejorar sus habilidades y obtener más y mejores resultados.
              </p>
            </div>
          </div>
        </Container>
        <PartnersHeroAnimation />
      </div>
      <div
        className="relative lg:sticky top-0 bg-white shadow-sm z-30"
        style={{
          backdropFilter: 'blur(20px) saturate(120%)',
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
        }}
      >
        <Container className="overflow-x-auto overflow-y-hidden">
          <div className="flex items-center md:justify-center space-x-10">
            <ScrollSpy
              className="flex items-center space-x-4 lg:space-x-10 xl:space-x-12 lg:-mb-px"
              currentClassName="text-green-500 border-green-500"
              offset={-100}
              items={[
                'quien-se-asocia',
                'por-que-asociarse',
                'detalles-del-programa',
              ]}
            >
              <ScrollSpyItem
                anchor="quien-se-asocia"
                label="¿Quién se asocia con nosotros?"
                targetClassName="flex items-center border-b-2 border-transparent py-4 lg:py-6 leading-6 font-medium"
              />
              <ScrollSpyItem
                anchor="por-que-asociarse"
                label="¿Por qué asociarse con nosotros?"
                targetClassName="flex items-center border-b-2 border-transparent py-4 lg:py-6 leading-6 font-medium"
              />
              <ScrollSpyItem
                anchor="detalles-del-programa"
                label="Detalles del programa"
                targetClassName="flex items-center border-b-2 border-transparent py-4 lg:py-6 leading-6 font-medium"
              />
            </ScrollSpy>
            <div className="flex items-center">
              <a href="#aplicar" className="whitespace-nowrap flex items-center rounded-full bg-green-500 text-white font-semibold px-5 py-3 leading-3 focus:bg-green-600 hover:bg-green-600 focus:ring-4 focus:ring-green-800 focus:ring-opacity-20 transition ease-in-out duration-100" role="button">
                Aplica y hazte socio
              </a>
            </div>
          </div>
        </Container>
      </div>
      <div id="quien-se-asocia" className="relative z-10">
        <div className="py-10 lg:py-20 xl:py-32 space-y-10 lg:space-y-28 bg-gradient-to-b from-gray-100 to-white">
          <Container className="space-y-10 lg:space-y-20">
            <div className="flex flex-col items-center text-center space-y-4">
              <div>
                <svg className="w-20 h-20" width="48" height="48" viewBox="0 0 48 48">
                  <path className="fill-current text-green-200" d="M41,11V41H7V11H5V7H43v4H41Z" />
                  <path className="fill-current text-green-500 vi-accent" d="M38.5,18c-4.3,0-4.5-5-4.5-5a5,5,0,0,1-10,0,5,5,0,0,1-10,0s-0.2,5-4.5,5A4.5,4.5,0,0,1,5,13.5V11H43v2.5A4.5,4.5,0,0,1,38.5,18ZM22.01,35.01h-9V25.99h9v9.02ZM34.993,41h-8V25.99h8V41Z" />
                </svg>
              </div>
              <SecondaryTitle className="">
                ¿Quién se asocia con nosotros?
              </SecondaryTitle>
              <div className="grid grid-cols-1 lg:grid-cols-12">
                <Lead className="lg:col-span-8 lg:col-start-3">
                  Organizaciones, empresas y personas que impulsan la adopción de la
                  cultura ágil en toda la región hispanohablante.
                </Lead>
              </div>
            </div>
          </Container>
          <Container>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-7 space-y-10 lg:pr-10 lg:py-10">
                <div className="flex flex-col">
                  <SubHeading heading="Red de socios" />
                  <div className="space-y-4 lg:space-y-6">
                    <h3 className="text-3xl lg:pr-32 leading-relaxed font-extrabold md:font-semibold tracking-tight">
                      Construyendo alianzas duraderas para liderar el cambio              
                    </h3>
                    <div className="space-y-4">
                      <Paragraph className="text-lg leading-relaxed text-gray-700">
                        Una asociación con nosotros es la forma más inteligente para
                        influir en la comunidad hispanohablante apasionada por la cultura ágil.
                      </Paragraph>
                      <Paragraph className="text-lg leading-relaxed text-gray-700">
                        La asociación con AIBES te ayudará a generar nuevas oportunidades,
                        liderar el cambio hacia la adopción de la cultura ágil y expandirte
                        internacionalmente.
                      </Paragraph>
                    </div>
                  </div>
                </div>
                <LinkMore href="/socios">
                  Conoce a nuestros socios
                </LinkMore>
              </div>
              <div className="lg:col-span-5 relative">
                <div className="flex rounded-lg lg:rounded-xl shadow-lg lg:shadow-xl">
                  <Image
                    className="w-full h-full object-cover rounded-lg"
                    src="v1612308818/socios/red-de-socios_jpd5wp.jpg"
                    alt="Red de socios"
                    width={600}
                    height={700}
                  />
                </div>
              </div>
            </div>
          </Container>
        </div>
        <PartnersClientBrands />
        <div className="relative py-10 lg:py-20 xl:py-32 bg-gradient-to-tr from-gray-100 to-white">
          <Container className="space-y-10 lg:space-y-20 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5 relative">
                <div className="rounded-lg lg:rounded-xl shadow-lg lg:shadow-xl flex">
                  <Image
                    className="w-full h-full object-cover rounded-lg lg:rounded-xl"
                    src="v1612308818/socios/red-de-entrenadores_ntpxfp.jpg"
                    alt="Red de entrenadores"
                    width={700}
                    height={600}
                  />
                </div>
              </div>
              <div className="lg:col-span-7 space-y-10 lg:pl-10 lg:py-10">
                <div className="flex flex-col">
                  <SubHeading heading="Socios entrenadores" />
                  <div className="space-y-4 lg:space-y-6">
                    <h3 className="text-3xl leading-relaxed font-extrabold md:font-semibold tracking-tight">
                      Profesionales del más alto nivel que desarrollan su carrera con nuestro programa
                    </h3>
                    <Paragraph className="text-lg leading-relaxed text-gray-700">
                      Contamos con entrenadores con vasta experiencia y conocimientos demostrados.
                      Reconocidos referentes en la región por su contribución y desarrollo en
                      procesos de transformación organizacional.
                    </Paragraph>
                  </div>
                </div>
                <LinkMore href="/entrenadores">
                  Conoce a nuestros entrenadores
                </LinkMore>
              </div>
            </div>
          </Container>
        </div>
      </div>
      <div id="por-que-asociarse" className="py-10 lg:pt-20 xl:pt-32 bg-white relative z-20 bg-gradient-to-t from-gray-100 to-white">
        <Container className="space-y-10 lg:space-y-20">
          <div className="flex flex-col items-center text-center space-y-4">
            <div>
              <svg className="w-20 h-20" width="48" height="48" viewBox="0 0 48 48">
                <path className="fill-current text-green-200" d="M24,41L11,33V22H37V33ZM6,34a2.993,2.993,0,0,1-1-5.816V20l2,1v7.184A2.993,2.993,0,0,1,6,34Z" />
                <path className="fill-current text-green-500" d="M24,7.012l21.986,12.5L24,32.013,2.014,19.513Z" />
              </svg>
            </div>
            <SecondaryTitle className="">
              ¿Por qué asociarse con nosotros?
            </SecondaryTitle>
            <div className="grid grid-cols-1 lg:grid-cols-12">
              <Lead className="lg:col-span-8 lg:col-start-3">
                Nuestro programa de socios está diseñado para colaborar en la creación de
                nuevas oportunidades, liderar el cambio hacia la adopción de la cultura ágil
                y expandirte internacionalmente, principalmente a las comunidades hispanohablantes.
              </Lead>
            </div>
          </div>
          <div className="flex flex-col pt-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-7 space-y-6 lg:space-y-10 lg:pr-20 lg:py-10">
                <div className="space-y-4 lg:space-y-6">
                  <h3 className="text-xl lg:text-3xl leading-relaxed font-semibold tracking-tight">
                    Gran impacto para equipos de todos los tamaños y en cualquier lugar
                  </h3>
                  <Paragraph className="text-lg leading-relaxed text-gray-700">
                    Asociarte con nosotros significa contar profesionales competentes en la
                    adopción de la cultura ágil, con el respaldo de programas de entrenamientos
                    internacionales, entrenadores de primer nivel y certificaciones reconocidas
                    y válidas mundialmente.
                  </Paragraph>
                </div>
                <ul className="grid grid-cols-1 gap-3 text-lg text-gray-600 leading-7 lg:pr-20">
                  <li className="flex items-start space-x-2">
                    <Check className="text-green-500 mt-px" />
                    <div className="flex-1">
                      Acceso a materiales de calidad unificados
                    </div>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="text-green-500 mt-px" />
                    <div className="flex-1">
                      Acceso a entrenadores internacionales reconocidos como referentes en la región
                    </div>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="text-green-500 mt-px" />
                    <div className="flex-1">
                      Certificaciones reconocidas a nivel internacional, basadas en el Manifiesto Ágil
                    </div>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="text-green-500 mt-px" />
                    <div className="flex-1">
                      Acceso a la plataforma virtual en la que se utilizan métodos innovadores,
                      con materiales, programas, casos de estudio y buenas prácticas
                    </div>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="text-green-500 mt-px" />
                    <div className="flex-1">
                      Simuladores de los exámenes de certificación
                    </div>
                  </li>
                </ul>
              </div>
              <div className="lg:col-span-5 flex relative">
                <div className="flex rounded-lg lg:rounded-xl shadow-lg lg:shadow-xl">
                  <Image
                    className="w-full h-full object-cover rounded-lg lg:rounded-xl"
                    src="v1612308816/socios/programa-de-socios_p0hovd.jpg"
                    alt="Programa de socios"
                    width={700}
                    height={600}
                  />
                </div>
              </div>
            </div>
          </div>
          <PartnersTestimonials />
        </Container>
      </div>
      <div id="detalles-del-programa" className="py-10 lg:py-20 xl:py-32 bg-gradient-to-b from-gray-100 to-white relative z-10">
        <Container className="space-y-10 lg:space-y-20">
          <div className="flex flex-col items-center text-center space-y-4">
            <div>
              <svg className="w-20 h-20" width="48" height="48" viewBox="0 0 48 48">
                <path className="fill-current text-green-200" d="M29.624,43.785L25.381,32.472l7.778-7.778,2.075,8.735ZM4.168,18.329l10.357-5.611,8.735,2.075-7.778,7.778ZM5.624,34.991c1.92-4.929,5.24-2.274,5.24-2.274l2.222,2.217,2.2,2.193s2.627,3.28-2.295,5.213a12.725,12.725,0,0,1-4.578.376c-0.815-.076-3.537,1.6-4.347,1.222-0.39-.814,1.288-3.537,1.207-4.355A12.681,12.681,0,0,1,5.624,34.991Z" />
                <path className="fill-current text-green-500" d="M10.7,24.537C29.323,6.122,43.983,3.986,43.983,3.986S41.823,18.484,23.2,36.9c0,0-4.841,5.327-11.38-1.14A8.244,8.244,0,0,1,10.7,24.537Z" />
                <path className="fill-current text-green-200" d="M27.646,20.336a4.454,4.454,0,1,1-6.346-.058A4.512,4.512,0,0,1,27.646,20.336Z" />
              </svg>
            </div>
            <SecondaryTitle>
              Únete a nuestro programa de socios
            </SecondaryTitle>
            <div className="grid grid-cols-1 lg:grid-cols-12">
              <Lead className="lg:col-span-8 lg:col-start-3">
                Trabajemos juntos para acelerar el impacto en la adopción de la cultura ágil
                de nuestros clientes y para ayudarles a obtener más y mejores resultados.
              </Lead>
            </div>
          </div>
          <div className="rounded-lg bg-white py-10 shadow-lg lg:shadow-xl">
            <PartnersApplicationSteps />
          </div>
        </Container>    
      </div>
      <div id="aplicar" className="py-10 lg:py-20 xl:py-32 relative z-10 overflow-hidden bg-green-100">
        <Container maxWidth="max-w-screen-lg" className="relative z-10">
          <div className="rounded-2xl bg-white shadow-2xl p-4 lg:p-6 -mx-4 md:mx-0">
            <div className="py-10 space-y-10 lg:space-y-12 w-full">
              <h3 className="text-lg lg:text-3xl text-center font-semibold tracking-tight">
                Aplica hoy y hazte nuestro socio
              </h3>
              <div className="lg:px-10">
                <PartnerApplyForm />
              </div>
            </div>
          </div>
        </Container>
        <div className="absolute left-0 bottom-0 right-0 h-2/4 pointer-events-none">
          <div className="absolute inset-0 bg-gray-900 transform -skew-y-6" />
          <div className="absolute left-0 bottom-0 right-0 h-2/4 bg-gray-900" />
        </div>
      </div>
    </div>
  );
}

PartnersProgramPage.layoutProps = {
  stickyHeader: false, 
};
